import { useEffect, useState } from 'react'
import {
  useConnectedWallet,
  useLCDClient,
  useWallet,
} from '@terra-money/wallet-provider'
import { accountStore, networkStore, taskStore } from 'stores'
import { contractStore } from 'stores/contractStore'
import { poolService } from 'services/poolService'
import { baseLcdService } from 'services/base/baseLcdService'
import { oraclePriceService } from 'services/oracleService'
import { assetService } from 'services/assetService'
import { assets } from 'constants/assets'
import { defaultNetworkName, NetworkName } from 'constants/network'
import { contractAddresses } from 'constants/address'
import { reaction } from 'mobx'
import { schedulerService } from 'services/schedulerService'
import useWebSocket from 'react-use-websocket'
import { terraService } from 'services/terraService'
import { gatewayService } from 'services/gatewayService'
import { RouteName, TaskNameId } from 'constants/constants'
import { useLocation } from 'react-router-dom'
import { irmService } from 'services/irmService'
import { AmplitudeUtils } from 'utils/AmplitudeUtils'

export const useSetup = () => {
  const { pathname } = useLocation()
  const [isReady, setIsReady] = useState(false)
  const connectedWallet = useConnectedWallet()
  const lcd = useLCDClient()
  const { network } = useWallet()
  const { lastMessage, sendJsonMessage } = useWebSocket(
    'wss://observer.terra.dev',
  )

  // subscript new_block
  useEffect(() => {
    // TODO: remove event mainnet on chain testnet
    sendJsonMessage({
      subscribe: 'new_block',
      chain_id: network.chainID,
    })
  }, [network.chainID, sendJsonMessage])

  // update new_block
  useEffect(() => {
    if (!lastMessage) return
    const _data = JSON.parse(lastMessage.data)
    if (_data.chain_id !== network.chainID) return

    networkStore.setBlock({
      chainId: network.chainID,
      height: parseInt(_data.data.block.header.height),
      timeStamp: new Date(_data.data.block.header.time).getTime(),
    })
  }, [lastMessage, network.chainID])

  // fetch new data every block
  useEffect(() => {
    AmplitudeUtils.init()
    reaction(
      () => networkStore.block.height,
      () => {
        schedulerService.subscribe()
      },
    )
    reaction(
      () => connectedWallet,
      () => {
        if (!connectedWallet) {
          schedulerService.unsubscribe()
        }
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const fetchEligible = async () => {
      if (!connectedWallet) return
      accountStore.setEligible(accountStore.eligible, true)
      const res = await gatewayService.getWhitelistStatus(
        connectedWallet.walletAddress,
      )
      accountStore.setEligible(res, false)
      setIsReady(true)
    }

    fetchEligible()
    if (connectedWallet) {
      accountStore.setAddress(connectedWallet.walletAddress)

      // Set LCD
      poolService.initLCD(lcd)
      baseLcdService.initLCD(lcd)
      gatewayService.initLCD(lcd)
      oraclePriceService.initLCD(lcd)
      irmService.initLCD(lcd)

      // Set contract address
      contractStore.initContractStore(network.name)
      poolService.initAddress(
        contractStore.addresses.pilotPool,
        connectedWallet.walletAddress,
      )
      gatewayService.initAddress(contractStore.addresses.pilotGateway)
      assetService.initAssets(assets[network.name as NetworkName])
      taskStore.clear()
      taskStore.updateTask(terraService.fetchBalance, TaskNameId.balance)
    } else {
      accountStore.setAddress('')
      contractStore.initContractStore(defaultNetworkName)
      poolService.initAddress(
        contractAddresses[defaultNetworkName].pilotPool,
        '',
      )
      assetService.initAssets(assets[defaultNetworkName])
      taskStore.clear()
    }
    if (![RouteName.pool, RouteName.default].includes(pathname as RouteName)) {
      setIsReady(true)
    }
  }, [connectedWallet, lcd, network.name, pathname])

  useEffect(() => {
    let timeId = setTimeout(() => setIsReady(true), 1000)

    return () => {
      clearTimeout(timeId)
    }
  }, [])

  return { isReady }
}
